import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Target } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useDemoSession } from "@/hooks/useDemoSession";
import { demoLogin, type DemoPersona } from "@/api/auth";

const PERSONA_LABELS: Record<DemoPersona, string> = {
  hr_admin: "HR Admin",
  manager: "Manager",
  employee: "Employee",
  executive: "Executive",
};

const PERSONA_LANDINGS: Record<DemoPersona, string> = {
  hr_admin: "/",
  manager: "/my/team",
  employee: "/my-documents",
  executive: "/agents/atlas",
};

const SIGNUP_URL = "https://lenavio.com/signup";

/**
 * Sticky blue bar shown when the access token carries the `demo` claim.
 * Mounts inside AppShell only (never on /demo itself), and disappears the
 * moment the session ends or the token is swapped for a real one.
 */
export function DemoBanner() {
  const { isDemo, persona } = useDemoSession();
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const switchPersona = useCallback(
    async (next: DemoPersona) => {
      if (switching) return;
      setSwitching(true);
      try {
        const res = await demoLogin(next);
        setSession(res.access_token, res.user);
        window.dispatchEvent(new Event("demo:session"));
        navigate(PERSONA_LANDINGS[next]);
      } finally {
        setSwitching(false);
        setOpen(false);
      }
    },
    [switching, setSession, navigate]
  );

  if (!isDemo) return null;
  const label = persona ? PERSONA_LABELS[persona] : "Guest";

  return (
    <div
      className="sticky top-0 z-50 flex h-12 items-center justify-between gap-4 px-4 text-sm text-white shadow-md"
      style={{ backgroundColor: "#1B18FF" }}
      data-demo-banner
    >
      <div className="flex items-center gap-2 font-medium">
        <Target className="h-4 w-4" aria-hidden />
        <span className="truncate">
          You&apos;re exploring Lenavio as an {label}
        </span>
      </div>

      <div className="hidden flex-1 text-center text-white/90 sm:block">
        Demo mode — changes won&apos;t be saved
      </div>

      <div className="flex items-center gap-2">
        <a
          href={SIGNUP_URL}
          className="rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-[#1B18FF] transition hover:bg-white/90"
        >
          Sign up free →
        </a>

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-1 rounded-md border border-white/30 px-2 py-1.5 text-xs font-medium text-white hover:bg-white/10"
          >
            Switch role <ChevronDown className="h-3 w-3" aria-hidden />
          </button>
          {open && (
            <div
              role="menu"
              className="absolute right-0 mt-1 w-40 overflow-hidden rounded-md border border-black/10 bg-white text-text-primary shadow-lg"
            >
              {(Object.keys(PERSONA_LABELS) as DemoPersona[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  role="menuitem"
                  disabled={switching}
                  onClick={() => switchPersona(p)}
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-100 disabled:opacity-50"
                >
                  {PERSONA_LABELS[p]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
